import React, { useState } from 'react';

import './Faq.scss';

const faqData = [
  {
    category: 'For Coaches',
    questions: [
      {
        question: 'How do I get more details about a player?',
        answer: "PlaySportUSA includes all important details on each player's profile, including videos, stats, and performance analysis. If you need any additional information, please email us at contact@playsportusa.com and we'll provide the specific details you need."
      },
      {
        question: "How do I access player profiles?",
        answer: 'Click on "Available Players" in the navigation menu to see all available athletes. Use the filters to narrow down by sport, gender, position, and graduation year. Click on any player card to view their full profile with videos and detailed information.'
      },
      {
        question: 'How do I contact a player?',
        answer: 'To contact a player directly, visit their profile page and click the "Contact this Player" button. Your message will be sent directly to the player\'s email, and our team will be CC\'d to help facilitate the connection.'
      },
      {
        question: 'Is PlaySportUSA a year-round service?',
        answer: 'Yes! We add new athletes every 1-2 weeks and continuously update videos and information. Our database is active throughout the year, so you can always find fresh talent regardless of the recruiting season.'
      },
      {
        question: 'Can I save players to review later?',
        answer: 'Absolutely! Click the heart icon on any player card or profile to add them to your Favorites list. You can access your saved players anytime from the "Favorite Players" section in the menu.'
      },
      {
        question: 'What is the Compare Players feature?',
        answer: 'Compare Players lets you select multiple athletes and view them side by side. Click the "Compare" button on any player card to add them to your comparison list. You can compare stats, physical attributes, academic info, and more — all in one view. It\'s a great way to narrow down your recruiting choices.'
      },
      {
        question: 'How do Saved Searches work?',
        answer: 'When you apply filters on the Available Players page, you can save that search for quick access later. Click "Save Search" above the player list, give it a name, and it will appear in your Saved Searches sidebar. One click loads the exact same filters — no need to set them up again.'
      },
      {
        question: 'What is Recently Viewed?',
        answer: 'The Recently Viewed section on the Available Players page shows the last players you visited. It helps you quickly get back to profiles you were reviewing without having to search again.'
      },
      {
        question: 'Can I get personalized player recommendations?',
        answer: 'Yes! PlaySportUSA analyzes your activity — the sports you search for, players you view and favorite — and suggests athletes that match your preferences. Check the "Recommended" section in the menu to discover new talent tailored to your recruiting interests.'
      },
      {
        question: 'What sports does PlaySportUSA cover?',
        answer: 'We currently cover Volleyball, Beach Volleyball, Basketball, Soccer, Swimming, Tennis, Track & Field, Ice Hockey, and Water Polo. We\'re constantly expanding to include more sports based on demand.'
      }
    ]
  },
  // {
  //   category: 'For Athletes',
  //   questions: [
  //     {
  //       question: 'How can I create a profile on PlaySportUSA?',
  //       answer: 'To create a profile, please contact us at contact@playsportusa.com with your sport, current school/club, and a brief description of your athletic background. Our team will guide you through the process of setting up your professional profile.'
  //     },
  //     {
  //       question: 'What information should I include in my profile?',
  //       answer: 'Your profile should include: basic information (height, weight, position), academic details (GPA, test scores), athletic achievements, current club/school, video highlights, and any relevant accomplishments. The more complete your profile, the better your chances of being noticed by coaches.'
  //     },
  //     {
  //       question: 'How do coaches find my profile?',
  //       answer: 'Coaches can search for athletes using filters like sport, gender, graduation year, and position. Make sure your profile is complete and your videos are up-to-date to maximize visibility. We also actively promote athletes to our network of college coaches.'
  //     },
  //     {
  //       question: 'Can I update my profile after it\'s created?',
  //       answer: 'Yes! We encourage athletes to keep their profiles updated with new videos, improved stats, and recent accomplishments. Contact us anytime to update your information.'
  //     }
  //   ]
  // },
  {
    category: 'General',
    questions: [
      {
        question: 'Is PlaySportUSA free to use?',
        answer: 'For coaches, browsing player profiles and contacting athletes is completely free. Athletes may have different options depending on the level of profile and promotion they require. Contact us for more details.'
      },
      {
        question: 'How do I reset my password?',
        answer: 'Click on "Login" and then "Forgot Password". Enter your email address and we\'ll send you a link to reset your password. If you have any issues, contact us at contact@playsportusa.com.'
      },
      {
        question: 'Who can I contact for support?',
        answer: 'For any questions, technical issues, or support needs, please email us at contact@playsportusa.com or call (+48) 794 024 963. We typically respond within 24 hours.'
      }
    ]
  }
];

const FaqItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className={`faq-item ${isOpen ? 'faq-item--open' : ''}`}>
      <button 
        type="button" 
        className="faq-item__question"
        onClick={onClick}
        aria-expanded={isOpen}
      >
        <span>{question}</span>
        <i className={`fas fa-chevron-down faq-item__icon`} />
      </button>
      <div className="faq-item__answer-wrapper">
        <div className="faq-item__answer">
          <p>{answer}</p>
        </div>
      </div>
    </div>
  );
};

const Faq = () => {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <div className="faq">
      <div className="faq__header">
        <h1 className="faq__title">Frequently Asked Questions</h1>
        <p className="faq__subtitle">
          Find answers to common questions about PlaySportUSA
        </p>
      </div>

      {faqData.map((category, categoryIndex) => (
        <div key={category.category} className="faq__category">
          <h2 className="faq__category-title">
            <i className={`fas ${
              category.category === 'For Coaches' ? 'fa-chalkboard-teacher' :
              category.category === 'For Athletes' ? 'fa-running' : 'fa-info-circle'
            }`} />
            {category.category}
          </h2>
          <div className="faq__list">
            {category.questions.map((item, questionIndex) => (
              <FaqItem
                key={questionIndex}
                question={item.question}
                answer={item.answer}
                isOpen={openItems[`${categoryIndex}-${questionIndex}`]}
                onClick={() => toggleItem(categoryIndex, questionIndex)}
              />
            ))}
          </div>
        </div>
      ))}

      <div className="faq__contact">
        <h3>Still have questions?</h3>
        <p>We're here to help! Reach out to our team.</p>
        <a href="mailto:contact@playsportusa.com" className="faq__contact-btn">
          <i className="fas fa-envelope" /> Contact Us
        </a>
      </div>
    </div>
  );
};

export default Faq;
